import { Point } from '../../../platform/geom/Point';
import { Rect } from '../../../platform/geom/Rect';
import { TextAlignment, TextSettings } from '../../canvas/entities/TextConventions';
import { TextRuler } from '../../canvas/visualization/TextRuler';
import { Tag } from './Tag';

export const TagTextSettings: TextSettings = {
    fontColor: '#fff',
    fontFamily: 'Rubik',
    fontSize: 10,
    maxFontSize: 10,
    fontStyle: 'normal',
    fontWeight: 450,
    lineHeight: 1,
    textAlignH: TextAlignment.start,
    textAlignV: TextAlignment.middle,
    textMargin: [1, 2],
};

export type TagLayoutEntries = Record<string, Rect>;
export type TagLayout = TagLayoutEntries & { all: Rect };
export type TagLike = { id: string, icon?: boolean } & Partial<Pick<Tag, 'text'>>;

const wrapAndUnion = (entries: TagLayoutEntries) => ({
    ...entries,
    all: Rect.fromMany(Object.values(entries)),
});

export const TagLayout = (tags: TagLike[], flowBounds: Rect, avoidBounds: Rect[] = []): TagLayout => {

    const layout = {} as TagLayoutEntries;
    let br = flowBounds.bottomRight().subtract(5);

    // Just putting the 100 max in as a safety valve for now.
    let cb = 0;
    for (let i = 0; i < tags.length && cb < 50; i++) {
        const t = tags[i];

        const { w: textWidth } = TextRuler.measureSize(t.text, TagTextSettings);

        const size = new Point(
            !!t.icon ? (12 + textWidth) : textWidth,
            18,
        );
        const bounds = new Rect(br.x - size.w, br.y - size.h, size.w, size.h);

        // Is it within the bounds and not in the avoid list?
        if (flowBounds.contains(bounds) && !avoidBounds.some(b => b.intersects(bounds))) {
            layout[t.id] = bounds;
            br = new Point(br.x - size.w - 5, br.y);
        }
        else {
            // If not in bounds, move the base point and repeat the loop step
            br = new Point(flowBounds.right, br.y - size.h).subtract(5);
            i--;
        }

        cb++;
    }

    return wrapAndUnion(layout);
};

export type TagLayoutOpts = {
    flow?: 'ltr' | 'rtl',
    gap?: number,
    textSettings?: Partial<TextSettings>,
};

export const LtrTagLayout = (tags: TagLike[], bounds: Rect, opts: TagLayoutOpts = {}): TagLayout => {
    const flow = opts.flow ?? 'ltr';
    const gap = opts.gap ?? 5;
    const textSettings = { ...TagTextSettings, ...(opts.textSettings ?? {}) };

    const entries = {} as TagLayoutEntries;
    const flowMul = flow === 'ltr' ? 1 : -1;
    let cursor = flow === 'ltr' ? bounds.bottomLeft() : bounds.bottomRight();

    for (let i = 0; i < tags.length; i++) {
        const t = tags[i];
        const tm = TextRuler.measureSize(t.text, textSettings);
        const size = tm.add([
            !!t.icon ? 12 : 0,
            6,
        ]);

        const plot = new Rect(cursor.x, cursor.y - size.h, size.w, size.h);
        for (let j = 0; true; j++) {
            if (j >= 50) return wrapAndUnion(entries);
            if (!bounds.contains(plot)) {
                // If not in bounds, move the cursor up a line, and reset start
                cursor = new Point(cursor.x, cursor.y - size.h).add([0, gap]);
            }
            else {
                break;
            }
        }

        entries[t.id] = plot;
        cursor = cursor.add([(size.w + gap) * flowMul, 0]);
    }

    if (!tags.length) {
        return { all: new Rect(cursor.x, cursor.y, bounds.width, 0), ...entries };
    }

    return wrapAndUnion(entries);
};
